import { getCategoryProfile } from "../category-profiles";
import { getCategoryDisplayBreadcrumb, type TaxonomyCategoryGroup } from "../category-taxonomy";
import type { CategoryTaxonomyLoadStatus } from "./public-config";
import type { IntakeCategoryId } from "../types";

export function CategoryTaxonomyLoadScreen({ status }: { status: CategoryTaxonomyLoadStatus }) {
  const isError = status === "error";
  return (
    <main className="resume-screen">
      <section className="resume-card">
        <p className="eyebrow">크리에이트립 입점을 위한 상품요청서</p>
        <h1>{isError ? "카테고리 설정을 불러오지 못했어요" : "카테고리 설정을 불러오고 있어요"}</h1>
        <p>{isError ? "잠시 후 새로고침해 주세요. 카테고리 설정을 확인하기 전에는 설문을 시작하지 않습니다." : "공개된 카테고리 tree를 확인한 뒤 설문을 시작합니다."}</p>
      </section>
    </main>
  );
}

export function CategoryLanding({ selectedCategoryId, onSelect, categoryGroups }: { selectedCategoryId: IntakeCategoryId; onSelect: (categoryId: IntakeCategoryId) => void; categoryGroups: TaxonomyCategoryGroup[] }) {
  return (
    <main className="category-landing">
      <a className="landing-admin-link" href="/admin">관리자 화면 ↗</a>
      <header className="landing-hero">
        <p className="eyebrow">크리에이트립 입점을 위한</p>
        <h1>상품요청서 작성</h1>
        <p>
          카테고리 담당자는 아래 링크 중 하나만 파트너에게 전달하면 됩니다. 링크를 열면 해당 유형에 맞는 상품요청서가 바로 시작됩니다.
        </p>
      </header>
      <CategorySelectionPanel selectedCategoryId={selectedCategoryId} onSelect={onSelect} categoryGroups={categoryGroups} useLinks />
    </main>
  );
}

export function CategorySelectionPanel({ selectedCategoryId, onSelect, categoryGroups, useLinks = false }: { selectedCategoryId: IntakeCategoryId; onSelect: (categoryId: IntakeCategoryId) => void; categoryGroups: TaxonomyCategoryGroup[]; useLinks?: boolean }) {
  return (
    <section className="category-selection" aria-label="상품 유형 선택">
      <div className="category-selection-head">
        <span>상품요청서 유형 선택</span>
        <strong>먼저 어떤 영역의 상품인지 골라주세요.</strong>
        <p>선택한 유형에 맞춰 표현, 필수 서류, 상품표, 주의사항 질문이 달라집니다. 메디컬·뷰티뿐 아니라 투어, 티켓, 체험, 교통, 통신, F&B, 쇼핑/쿠폰까지 지원합니다.</p>
      </div>
      <div className="category-domain-list">
        {categoryGroups.map((group) => (
          <section className="category-domain" key={group.id}>
            <div className="category-domain-copy">
              <h3>{group.label}</h3>
              <p>{group.description}</p>
            </div>
            <div className="category-card-grid">
              {group.options.map((option) => {
                const selected = selectedCategoryId === option.id;
                const content = (
                  <>
                    <span>{option.label}</span>
                    <strong>{option.description}</strong>
                    <small>{option.examples.join(" · ")}</small>
                  </>
                );
                return useLinks ? (
                  <a
                    className={`category-card ${selected ? "selected" : ""}`}
                    href={option.path}
                    key={option.id}
                    onClick={(event) => {
                      event.preventDefault();
                      onSelect(option.id);
                    }}
                  >
                    {content}
                  </a>
                ) : (
                  <button
                    className={`category-card ${selected ? "selected" : ""}`}
                    type="button"
                    key={option.id}
                    onClick={() => onSelect(option.id)}
                    aria-pressed={selected}
                  >
                    {content}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      {selectedCategoryId && (() => {
        const profile = getCategoryProfile(selectedCategoryId);
        const selectedGroup = categoryGroups.find((group) => group.options.some((option) => option.id === selectedCategoryId));
        const selectedOption = selectedGroup?.options.find((option) => option.id === selectedCategoryId);
        if (!profile) return null;
        return (
          <div className="category-selected-note" role="status">
            <strong>선택한 유형: {selectedGroup ? `${selectedGroup.label} > ${selectedOption?.label ?? profile.label}` : getCategoryDisplayBreadcrumb(selectedCategoryId)}</strong>
            <span>{profile.productNoun} 구성, {profile.expertNoun} 정보, 첨부 자료 안내가 이 유형에 맞춰 조정됩니다.</span>
          </div>
        );
      })()}
    </section>
  );
}
